export type ParsedIndexLine = {
  name: string;
  last: string;
  change?: string;
  pct?: string;
};

const stripBold = (text: string): string => text.replace(/\*\*/g, "");

const parseTickerBullet = (
  line: string
): { ticker: string; rest: string } | null => {
  const t = line.trim().replace(/^[-*]\s+/, "");
  const m = /^\*\*([^*]+)\*\*\s*[—–\-·|:]\s*(.*)$/.exec(t);
  if (m) return { ticker: m[1].trim(), rest: m[2].trim() };
  const m2 = /^([A-Z][A-Z0-9&.-]{1,20})\s*[—–\-·|:]\s*(.*)$/.exec(stripBold(t));
  if (m2) return { ticker: m2[1], rest: m2[2] };
  return null;
};

const leadingSign = (raw: string): "+" | "-" | "" => {
  const t = raw.trim();
  if (/^[+]/.test(t)) return "+";
  if (/^[-−–]/.test(t)) return "-";
  return "";
};

const stripLivePrefix = (raw: string): string =>
  raw.replace(/^\s*LIVE\s+/i, "").trim();

const uniqueSegments = (raw: string): string[] => {
  const segs = stripLivePrefix(raw)
    .split(/\s*[·|]\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of segs) {
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
};

/** Keep digits as-is; always show ASCII + or - (unsigned values get +). */
const normalizeSignedFigure = (
  raw: string,
  fallbackSign: "+" | "-" = "+"
): string => {
  let t = stripLivePrefix(raw);
  const inner = /^\((.*)\)$/.exec(t);
  if (inner) t = inner[1].trim();
  const hasPct = t.endsWith("%");
  if (hasPct) t = t.slice(0, -1).trim();
  const own = leadingSign(t);
  if (own) t = t.slice(1).trim();
  if (!t || /^LIVE$/i.test(t)) return "";
  return `${own || fallbackSign}${t}${hasPct ? "%" : ""}`;
};

const unpackChangePct = (raw: string): { change?: string; pct?: string } => {
  const t = stripLivePrefix(raw);
  const packed = /^(.*?)\s*\(([^)]*%[^)]*)\)\s*$/.exec(t);
  if (packed) {
    const change = packed[1].trim() || undefined;
    const pct = packed[2].trim() || undefined;
    if (change && /^LIVE$/i.test(change)) {
      return { pct };
    }
    return { change, pct };
  }
  if (t.includes("%")) return { pct: t };
  return { change: t };
};

const coerceMoveParts = (
  change?: string,
  pct?: string
): { change?: string; pct?: string } => {
  const segs = uniqueSegments([change, pct].filter(Boolean).join(" · "));
  let c: string | undefined;
  let p: string | undefined;
  for (const seg of segs) {
    const u = unpackChangePct(seg);
    if (u.change && u.pct) {
      c = u.change;
      p = u.pct;
      continue;
    }
    if (u.pct && !u.change) {
      p = u.pct;
      continue;
    }
    if (u.change && !c) c = u.change;
  }
  if (p) {
    const inner = /^\((.*)\)$/.exec(p.trim());
    if (inner) p = inner[1].trim();
  }
  return { change: c, pct: p };
};

export const parseIndexLine = (line: string): ParsedIndexLine | null => {
  const b = parseTickerBullet(line);
  if (!b) return null;
  const parts = uniqueSegments(
    b.rest.replace(/^(last|change|percentChange)\s*=\s*/gi, "")
  ).map((p) => p.replace(/^(last|change|percentChange)\s*=\s*/i, "").trim());
  const last = parts[0] || b.rest;
  const move = coerceMoveParts(parts[1], parts[2]);
  return { name: b.ticker, last, change: move.change, pct: move.pct };
};

export const formatIndexMove = (change?: string, pct?: string): string => {
  const { change: c, pct: p } = coerceMoveParts(change, pct);
  if (c && p) {
    const signedChange = normalizeSignedFigure(c);
    if (!signedChange) return "";
    const changeSign = leadingSign(signedChange) || "+";
    const signedPct = normalizeSignedFigure(p, changeSign);
    return `${signedChange} (${signedPct})`;
  }
  if (c) return normalizeSignedFigure(c);
  if (p) return `(${normalizeSignedFigure(p)})`;
  return "";
};
